import { createContext, useContext, type ReactNode } from "react"

/**
 * Auth-mode-agnostic identity for the UI. Two modes, picked by GET /api/config:
 *   "msal"     — the SPA signs in with MSAL and sends a Graph Bearer token (sharepoint-bot).
 *   "platform" — Container Apps / App Service built-in auth sits in front of the app; the
 *                browser holds only the platform session cookie and the backend reads the
 *                user (and Graph token) from headers the platform injects. Config-driven
 *                web agents use this mode.
 * Components use useIdentity() and never import MSAL directly, so a platform-mode build
 * never initializes MSAL (and needs none of its VITE_MSAL_* env vars).
 */

export interface UserInfo {
  /** Display name, if the identity provider sent one. */
  name?: string
  /** Login name / UPN. */
  username: string
}

export interface Identity {
  user: UserInfo | null
  /** Whether a user is signed in. Platform mode is always true (the platform blocks anonymous requests). */
  isAuthenticated: boolean
  signIn: () => void
  signOut: () => void
  /** Headers to attach to API calls (a Bearer token in MSAL mode, nothing in platform mode). */
  authHeaders: () => Promise<Record<string, string>>
}

const IdentityContext = createContext<Identity | null>(null)

export function IdentityProvider({ value, children }: { value: Identity; children: ReactNode }) {
  return <IdentityContext.Provider value={value}>{children}</IdentityContext.Provider>
}

export function useIdentity(): Identity {
  const identity = useContext(IdentityContext)
  if (!identity) throw new Error("useIdentity must be used inside IdentityProvider")
  return identity
}

/**
 * The API layer runs outside React, so the active mode registers its header source here
 * once at startup (see main.tsx).
 */
let headerSource: () => Promise<Record<string, string>> = async () => ({})

export function setAuthHeaderSource(source: () => Promise<Record<string, string>>) {
  headerSource = source
}

export function getAuthHeaders(): Promise<Record<string, string>> {
  return headerSource()
}

/**
 * Second delegated token for the `/work-iq` agent. MSAL mode registers a source; platform mode
 * has none (the platform only forwards a Graph token), so Work IQ is unavailable there.
 */
let workIqTokenSource: () => Promise<string | null> = async () => null

export function setWorkIqTokenSource(source: () => Promise<string | null>) {
  workIqTokenSource = source
}

export function getWorkIqToken(): Promise<string | null> {
  return workIqTokenSource()
}

interface AuthMeClaim {
  typ: string
  val: string
}

/**
 * Current platform-auth user from /.auth/me (served by the auth sidecar, same origin).
 * Returns null when the endpoint is missing or returns no session, e.g. running locally
 * without the platform in front.
 */
export async function fetchPlatformUser(): Promise<UserInfo | null> {
  try {
    const response = await fetch("/.auth/me", { credentials: "same-origin" })
    if (!response.ok) return null
    const body = (await response.json()) as Array<{ user_id?: string; user_claims?: AuthMeClaim[] }>
    const entry = body[0]
    if (!entry) return null
    const claim = (typ: string) => entry.user_claims?.find((c) => c.typ === typ)?.val
    const username = claim("preferred_username") ?? entry.user_id ?? ""
    return { name: claim("name"), username }
  } catch {
    return null
  }
}

/** Identity for platform mode — the platform owns sign-in, so signIn just re-enters its flow. */
export function platformIdentity(user: UserInfo | null): Identity {
  return {
    user,
    isAuthenticated: true,
    signIn: () => {
      window.location.href = `/.auth/login/aad?post_login_redirect_uri=${encodeURIComponent(window.location.pathname)}`
    },
    signOut: () => {
      window.location.href = "/.auth/logout?post_logout_redirect_uri=/"
    },
    authHeaders: async () => ({}),
  }
}
